import {
  Button,
  Col,
  DatePicker,
  Form,
  FormProps,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Table,
} from "antd";
import { LoanProductProps } from "interfaces";
import React, { useEffect, useState } from "react";
import { useLoanRequestMutation, useLoanScheduleMutation } from "store/api";
import { useGetLoanProductsQuery } from "store/api/requests/loan/getLoanProductsApiSlice";

type FieldType = {
  loanType: string;
  loanAmount: string;
  interestRate: string;
  loanTerm: string;
  documentName: string;
  documentDescription: string;
  loanDescription: string;
  loanPurpose: string;
  startDate: string;
  repaymentPeriod: number;
};

const LoanForm = ({ refetch }: { refetch: any }) => {
  const [form] = Form?.useForm();
  const [selectedLoan, setSelectedLoan] = React.useState<LoanProductProps>();
  const [loanRequest, { isLoading }] = useLoanRequestMutation();
  const [loanSchedule, { isLoading: loanSchedulesLoading }] =
    useLoanScheduleMutation();
  const [calculatedLoanSchedule, setCalculatedLoanSchedule] = React.useState(
    [],
  );

  const { data, isFetching } = useGetLoanProductsQuery("products");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loanProducts = data?.data?.data || [];

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loanProducts?.length > 0) {
      setSelectedLoan(loanProducts[0]);
    }
  }, [loanProducts]);

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    try {
      await loanRequest({
        ...values,
        loanTypeName: selectedLoan?.loanType,
      }).unwrap();
      notification.success({
        message: "Success",
        description: "Loan Requested Successfully",
      });
      window.location.reload();
      refetch();
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred",
      });
    }
  };

  const getLoanSchedule = async () => {
    try {
      const res = await loanSchedule({
        ...selectedLoan,
        interestRate: selectedLoan?.interestRate,
        repaymentEvery: form.getFieldValue("repaymentPeriod"),
        loanAmount: form.getFieldValue("loanAmount"),
      }).unwrap();

      setCalculatedLoanSchedule(res?.data);
      setOpen(true);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An error occurred",
      });
    }
  };

  return (
    <div>
      {" "}
      <Form
        form={form}
        name="basic"
        onFinish={onFinish}
        autoComplete="off"
        initialValues={{
          loanPurpose: selectedLoan?.purpose,
          loanType: selectedLoan?.loanType,
          repaymentPeriod: 1,
        }}
      >
        <Row gutter={[14, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Select Loan Type"
              layout="vertical"
              name="loanType"
              rules={[{ required: true, message: "Please select loan " }]}
              className="mb-12 sm:mb-14 "
            >
              <Select
                loading={isFetching}
                allowClear
                options={[
                  { value: "", label: "Select Loan Type" },
                  ...loanProducts?.map((product: LoanProductProps) => ({
                    value: product.id,
                    label: product.loanType,
                  })),
                ]}
                onChange={(value) => {
                  const selected = loanProducts.find(
                    (product: LoanProductProps) => product.id === value,
                  );

                  setSelectedLoan(selected);
                  form.setFieldsValue({
                    interestRate: selected?.interestRate,
                    repaymentPeriod: selectedLoan?.repaymentEvery,
                  });

                  // form.setFieldsValue({
                  //   ...form.getFieldsValue(),
                  //   ...selectedLoan,
                  // });
                }}
                placeholder="Select loan type"
                className="bg-slate-100 !w-full h-10"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Purpose of Loan"
              layout="vertical"
              name="loanPurpose"
              className="mb-12 sm:mb-14 "
            >
              <Select
                placeholder="Select loan purpose"
                className="bg-slate-100 w-full h-10"
                options={[
                  { label: "Business", value: "business" },
                  {
                    label: "Education",
                    value: "education",
                  },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Principal Amount"
              layout="vertical"
              name="loanAmount"
              initialValue={selectedLoan?.minLoanAmount || 0}
              rules={[
                { required: true, message: "Please enter loan amount" },
                {
                  validator: (_, value) => {
                    const numValue = Number(value);
                    if (
                      selectedLoan?.minLoanAmount !== undefined &&
                      numValue < selectedLoan.minLoanAmount
                    ) {
                      return Promise.reject(
                        `Minimum amount is ${selectedLoan.minLoanAmount.toLocaleString()} UGX`,
                      );
                    }
                    if (
                      selectedLoan?.maxLoanAmount !== undefined &&
                      numValue > selectedLoan.maxLoanAmount
                    ) {
                      return Promise.reject(
                        `Maximum amount is ${selectedLoan.maxLoanAmount.toLocaleString()} UGX`,
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="mb-12 sm:mb-14"
            >
              <Input
                type="number"
                className="bg-slate-100 h-10"
                suffix="UGX"
                min={selectedLoan?.minLoanAmount}
                max={selectedLoan?.maxLoanAmount}
                defaultValue={selectedLoan?.minLoanAmount || 0}
                onChange={(e) => {
                  const value = e.target.value ? Number(e.target.value) : 0;
                  let clampedValue = value;

                  if (
                    selectedLoan?.maxLoanAmount !== undefined &&
                    value > selectedLoan.maxLoanAmount
                  ) {
                    clampedValue = selectedLoan.maxLoanAmount;
                  } else if (
                    selectedLoan?.minLoanAmount !== undefined &&
                    value < selectedLoan.minLoanAmount
                  ) {
                    clampedValue = selectedLoan.minLoanAmount;
                  }

                  // Force clamped value into the field
                  e.target.value = clampedValue.toString();
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[14, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              className="mb-3"
              label="Loan Term"
              name="loanTerm"
              layout="vertical"
            >
              <Input className="bg-slate-100 h-10" suffix="Months" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Repayment Period"
              name="repaymentPeriod"
              layout="vertical"
              className="mb-12 sm:mb-14 "
            >
              <Input className="bg-slate-100 h-10" suffix={"Month"} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Interest Rate"
              layout="vertical"
              name="interestRate"
              rules={[
                { required: true, message: "Please enter interest rate" },
              ]}
              className="mb-12 sm:mb-14 "
            >
              <Input className="bg-slate-100 h-10" suffix="%" disabled />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[14, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Start Date"
              layout="vertical"
              name="startDate"
              rules={[{ required: true, message: "Please enter start date" }]}
              className="mb-12 sm:mb-14 "
            >
              <DatePicker
                className="bg-slate-100 h-10 w-full"
                placement="bottomRight"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="NIN Number"
              layout="vertical"
              name="documentName"
              getValueFromEvent={(e) =>
                (e?.target?.value ?? "")
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, "")
                  .slice(0, 14)
              }
              rules={[
                {
                  required: true,
                  message: "Please enter identification number",
                },
                {
                  pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{14}$/,
                  message:
                    "ID must be 14 characters long and include both letters and numbers",
                },
              ]}
              // help="National ID number is recommended"
              className="mb-12 sm:mb-14"
            >
              <Input
                className="w-full h-10"
                maxLength={14}
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item<FieldType>
              label="Loan Description / comment"
              layout="vertical"
              name="documentDescription"
              className="mb-12 sm:mb-14 "
            >
              <Input className="bg-slate-100 h-10" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[14, 16]} className="hidden">
          <Col xs={24} sm={12} md={8}>
            <Button
              disabled={form.getFieldValue("loanAmount") !== 0}
              htmlType="button"
              className="bg-slate-100 h-10 w-full"
              onClick={getLoanSchedule}
            >
              Loan Schedule Calculator
            </Button>
          </Col>
        </Row>
        <Row gutter={[14, 16]}>
          <Modal
            open={open}
            onCancel={() => {
              setOpen(false);
              setCalculatedLoanSchedule([]);
            }}
            onOk={() => {
              setOpen(false);
              setCalculatedLoanSchedule([]);
            }}
            width={1200}
            bodyProps={{ className: "overflow-auto" }}
          >
            {" "}
            <div>
              <Table
                loading={loanSchedulesLoading}
                dataSource={calculatedLoanSchedule}
                columns={[
                  {
                    title: "Payment Number",
                    dataIndex: "paymentNumber",
                    key: "paymentNumber",
                  },
                  {
                    title: "Payment Date",
                    dataIndex: "paymentDate",
                    key: "paymentDate",
                  },

                  {
                    title: "Principal",
                    dataIndex: "principal",
                    key: "principal",
                    render: (text: string) => (
                      <span>
                        {Number(text).toLocaleString("en", {
                          style: "currency",
                          currency: "UGX",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    ),
                  },
                  {
                    title: "Interest",
                    dataIndex: "interest",
                    key: "interest",
                    render: (text: string) => (
                      <span>
                        {Number(text).toLocaleString("en", {
                          style: "currency",
                          currency: "UGX",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    ),
                  },
                  {
                    title: "Payment Amount",
                    dataIndex: "paymentAmount",
                    key: "paymentAmount",
                    render: (text: string) => (
                      <span>
                        {Number(text).toLocaleString("en", {
                          style: "currency",
                          currency: "UGX",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    ),
                  },
                  {
                    title: "Balance",
                    dataIndex: "balance",
                    key: "balance",
                    render: (text: string) => (
                      <span>
                        {Number(text).toLocaleString("en", {
                          style: "currency",
                          currency: "UGX",
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </Modal>
        </Row>

        <Form.Item className="mb-3 text-center mt-4">
          <Button
            type="primary"
            htmlType="submit"
            className="w-auto !min-w-40 font-semibold"
            loading={isLoading}
          >
            Submit
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default LoanForm;
